/**
 * CSRF Token Helper — In-memory CSRF token management for cookie sessions
 *
 * Fetches CSRF token from backend and attaches to state-changing requests.
 * Token stored in module-scoped variable (not localStorage) for security.
 *
 * Pattern: Double-submit — backend sets `11to1_csrf` cookie and returns token
 * in response body. Frontend attaches token via `X-CSRF-Token` header.
 */

import { API_V2_URL } from './api';

/** Module-scoped CSRF token — never persisted to storage */
let csrfToken: string | null = null;

/** Whether a CSRF fetch is currently in-flight (dedup) */
let fetchPromise: Promise<string | null> | null = null;

/**
 * Fetch a CSRF token from the backend.
 * Sets the `11to1_csrf` cookie (via Set-Cookie) and returns token in body.
 * Deduplicates concurrent calls.
 */
export async function fetchCsrfToken(): Promise<string | null> {
  // Deduplicate concurrent calls
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const res = await fetch(`${API_V2_URL}/auth/csrf`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!res.ok) {
        csrfToken = null;
        return null;
      }

      const data = await res.json();
      csrfToken = data.csrfToken || null;
      return csrfToken;
    } catch {
      csrfToken = null;
      return null;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/** Get the current in-memory CSRF token (may be null if not yet fetched) */
export function getCsrfToken(): string | null {
  return csrfToken;
}

/** Clear the CSRF token (call on logout) */
export function clearCsrfToken(): void {
  csrfToken = null;
  fetchPromise = null;
}

/** Check if an HTTP method requires CSRF protection */
export function isStateChangingMethod(method?: string): boolean {
  if (!method) return false;
  const upper = method.toUpperCase();
  return upper === 'POST' || upper === 'PUT' || upper === 'PATCH' || upper === 'DELETE';
}

/**
 * Check if cookie sessions are currently active.
 * When a CSRF token is in memory, it means the backend accepted the session cookie
 * and issued a CSRF token — indicating cookie-based auth is working.
 */
export function isCookieSessionActive(): boolean {
  return csrfToken !== null;
}

/**
 * Synchronous check for CSRF cookie in document.cookie.
 * Used on page reload to detect if a cookie session may still be valid
 * (the CSRF cookie is non-httpOnly, so JS can read it).
 */
export function hasCsrfCookie(): boolean {
  try {
    return document.cookie.includes('11to1_csrf=');
  } catch {
    return false;
  }
}

/**
 * Read the CSRF cookie value from document.cookie (non-httpOnly by design).
 * Fallback for when the in-memory token was never fetched this page load —
 * the double-submit check only requires header === cookie.
 */
export function readCsrfCookie(): string | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)11to1_csrf=([^;]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}
